import { LogOut, User, Settings, ChevronDown } from "lucide-react";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { Button } from "@/components/ui/button";
import { useLocation } from "wouter";
import { useState } from "react";

interface UserDropdownMenuProps {
  userName: string;
  userInitials: string;
  /** Optional avatar URL - falls back to initials if not provided */
  userAvatar?: string | null;
  onLogout: () => void;
  isLoggingOut: boolean;
}

export function UserDropdownMenu({
  userName,
  userInitials,
  userAvatar,
  onLogout,
  isLoggingOut,
}: UserDropdownMenuProps) {
  const [, setLocation] = useLocation();
  const [open, setOpen] = useState(false);

  const handleProfileClick = () => {
    setOpen(false);
    setLocation("/configuracoes");
  };

  const handleSettingsClick = () => {
    setOpen(false);
    setLocation("/configuracoes");
  };

  const handleLogoutClick = () => {
    setOpen(false);
    onLogout();
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          className="flex items-center gap-[10px] hover:opacity-80 transition-opacity"
          aria-label="Menu do usuário"
        >
          {/* Avatar - Image or Initials fallback */}
          {userAvatar ? (
            <img
              src={userAvatar}
              alt={userName}
              className="w-[22px] h-[22px] rounded-full object-cover"
              onError={(e) => {
                // Hide image on error, show initials instead
                e.currentTarget.style.display = "none";
                e.currentTarget.nextElementSibling?.classList.remove("hidden");
              }}
            />
          ) : null}
          <div
            className={`w-[22px] h-[22px] rounded-full bg-primary flex items-center justify-center text-white font-semibold text-xs ${
              userAvatar ? "hidden" : ""
            }`}
          >
            {userInitials}
          </div>

          <span className="text-white text-sm font-medium hidden md:block">
            {userName}
          </span>

          <svg
            className="h-4 w-4 text-white"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M19 9l-7 7-7-7"
            />
          </svg>
        </button>
      </PopoverTrigger>

      <PopoverContent
        align="end"
        className="w-40 bg-[#15203C] border-gray-600 p-1"
      >
        <div className="flex flex-col gap-1">
          <Button
            variant="ghost"
            className="justify-start text-white hover:bg-white/10"
            onClick={handleProfileClick}
          >
            <User className="" />
            Meu Perfil
          </Button>

          <Button
            variant="ghost"
            className="justify-start text-white hover:bg-white/10"
            onClick={handleSettingsClick}
          >
            <Settings className="" />
            Configurações
          </Button>
          
          <Button
            variant="ghost"
            className="justify-start text-white hover:bg-white/10"
            onClick={handleLogoutClick}
            disabled={isLoggingOut}
          >
            <LogOut className="" />
            {isLoggingOut ? "Saindo..." : "Sair"}
          </Button>
        </div>
      </PopoverContent>
    </Popover>
  );
}
